import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Loader2, Users, Globe2, Flame, CalendarClock, MailX, LineChart as LineChartIcon } from 'lucide-react';
import {
    ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
    PieChart, Pie, Cell, LineChart, Line, Legend,
} from 'recharts';
import { toast } from 'sonner';
import { NET_COLLECTIONS, NET_STATUSES } from './collections';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PIE_COLORS = ['#C8553D', '#588B8B', '#F28F3B', '#2D3A3A', '#B8B08D', '#A44A3F'];
const KIND_LABELS = { call: 'Appels', email: 'Emails', meeting: 'Réunions', event: 'Événements', note: 'Notes' };

const Kpi = ({ icon: Icon, label, value, sub, testid }) => (
    <div className="rounded-xl border border-border bg-surface p-4" data-testid={testid}>
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-foreground/50 font-semibold">
            <Icon size={12} className="text-primary" /> {label}
        </div>
        <div className="font-heading text-3xl mt-1.5">{value ?? '—'}</div>
        {sub && <div className="text-[11px] text-foreground/50 mt-0.5">{sub}</div>}
    </div>
);

const Panel = ({ title, children, testid }) => (
    <div className="rounded-xl border border-border bg-surface p-5" data-testid={testid}>
        <h3 className="font-heading text-lg mb-4">{title}</h3>
        {children}
    </div>
);

export default function NetworkAnalytics({ auth }) {
    const api = useMemo(() => axios.create({ baseURL: API, headers: auth?.headers || {} }), [auth]);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get('/network/analytics')
            .then(r => setData(r.data))
            .catch(() => toast.error('Chargement des analytics impossible'))
            .finally(() => setLoading(false));
    }, [api]);

    if (loading) return <div className="flex items-center justify-center py-20" data-testid="analytics-loading"><Loader2 className="animate-spin" /></div>;
    if (!data) return <div className="text-center py-20 text-foreground/50">Aucune donnée</div>;

    const k = data.kpis;
    const collectionData = Object.entries(data.by_collection || {}).map(([key, value]) => ({
        name: NET_COLLECTIONS[key]?.label || key, value,
    })).sort((a, b) => b.value - a.value);
    const continentData = Object.entries(data.by_continent || {}).map(([name, value]) => ({ name, value }));
    const countryData = (data.top_countries || []).map(([name, value]) => ({ name, value }));
    const statusData = Object.entries(data.by_status || {}).map(([key, value]) => ({
        name: NET_STATUSES[key]?.label || key, value,
    }));
    const kindData = Object.entries(data.interactions_by_kind || {}).map(([key, value]) => ({
        name: KIND_LABELS[key] || key, value,
    }));
    const campaigns = (data.campaign_performance || []).filter(c => c.total > 0);

    return (
        <div className="space-y-6" data-testid="network-analytics">
            {/* KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Kpi icon={Users} label="Contacts" value={k.total_contacts} sub={`${k.unsubscribed} désabonné(s)`} testid="analytics-kpi-contacts" />
                <Kpi icon={Globe2} label="Couverture géo" value={`${k.countries_covered} pays`} sub={`${k.continents_covered} continents`} testid="analytics-kpi-geo" />
                <Kpi icon={Flame} label="Interactions CRM" value={k.interactions_total} sub={`Score moyen réseau : ${k.avg_score ?? '—'}`} testid="analytics-kpi-interactions" />
                <Kpi icon={CalendarClock} label="Tâches" value={k.tasks_open} sub={`${k.tasks_done} terminée(s)`} testid="analytics-kpi-tasks" />
            </div>

            <div className="grid lg:grid-cols-2 gap-4">
                {/* Contacts par collection */}
                <Panel title="Contacts par collection" testid="analytics-collections">
                    <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={collectionData} layout="vertical" margin={{ left: 40 }}>
                            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                            <XAxis type="number" fontSize={11} />
                            <YAxis type="category" dataKey="name" fontSize={11} width={110} />
                            <Tooltip />
                            <Bar dataKey="value" name="Contacts" fill="#C8553D" radius={[0, 4, 4, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </Panel>

                {/* Répartition par continent */}
                <Panel title="Répartition par continent" testid="analytics-continents">
                    <ResponsiveContainer width="100%" height={280}>
                        <PieChart>
                            <Pie data={continentData} dataKey="value" nameKey="name" cx="50%" cy="50%"
                                 outerRadius={95} label={({ name, value }) => `${name} (${value})`} fontSize={11}>
                                {continentData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                            </Pie>
                            <Tooltip />
                        </PieChart>
                    </ResponsiveContainer>
                </Panel>

                {/* Top pays */}
                <Panel title="Top 15 pays" testid="analytics-countries">
                    <ResponsiveContainer width="100%" height={320}>
                        <BarChart data={countryData} layout="vertical" margin={{ left: 40 }}>
                            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                            <XAxis type="number" fontSize={11} />
                            <YAxis type="category" dataKey="name" fontSize={10} width={110} />
                            <Tooltip />
                            <Bar dataKey="value" name="Contacts" fill="#588B8B" radius={[0, 4, 4, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </Panel>

                {/* Pipeline + interactions */}
                <div className="space-y-4">
                    <Panel title="Pipeline relationnel" testid="analytics-status">
                        <ResponsiveContainer width="100%" height={130}>
                            <BarChart data={statusData}>
                                <XAxis dataKey="name" fontSize={11} />
                                <YAxis fontSize={11} allowDecimals={false} />
                                <Tooltip />
                                <Bar dataKey="value" name="Contacts" fill="#F28F3B" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </Panel>
                    <Panel title="Interactions par type" testid="analytics-kinds">
                        {kindData.length === 0 ? (
                            <div className="text-sm text-foreground/50 py-6 text-center">Aucune interaction enregistrée pour l'instant.</div>
                        ) : (
                            <ResponsiveContainer width="100%" height={130}>
                                <BarChart data={kindData}>
                                    <XAxis dataKey="name" fontSize={11} />
                                    <YAxis fontSize={11} allowDecimals={false} />
                                    <Tooltip />
                                    <Bar dataKey="value" name="Interactions" fill="#2D3A3A" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </Panel>
                </div>
            </div>

            {/* Évolution du réseau */}
            <Panel title="Évolution du réseau dans le temps" testid="analytics-growth">
                <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={data.growth || []}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" fontSize={11} />
                        <YAxis fontSize={11} allowDecimals={false} />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="total" name="Total cumulé" stroke="#C8553D" strokeWidth={2} dot />
                        <Line type="monotone" dataKey="added" name="Ajouts du mois" stroke="#588B8B" strokeWidth={2} dot />
                    </LineChart>
                </ResponsiveContainer>
            </Panel>

            {/* Performance campagnes */}
            <Panel title="Performance des campagnes email" testid="analytics-campaigns">
                {campaigns.length === 0 ? (
                    <div className="text-sm text-foreground/50 py-6 text-center flex items-center justify-center gap-2">
                        <MailX size={15} /> Aucune campagne envoyée pour l'instant.
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-border text-left text-[11px] uppercase tracking-wider text-foreground/50">
                                <th className="px-2 py-2">Campagne</th>
                                <th className="px-2 py-2 text-right">Envoyés</th>
                                <th className="px-2 py-2 text-right">Ouvertures</th>
                                <th className="px-2 py-2 text-right">Clics</th>
                                <th className="px-2 py-2 text-right">Désabos</th>
                            </tr>
                        </thead>
                        <tbody>
                            {campaigns.map((c, i) => (
                                <tr key={i} className="border-b border-border/50 last:border-0">
                                    <td className="px-2 py-2 font-medium">{c.name}</td>
                                    <td className="px-2 py-2 text-right tabular-nums">{c.sent}</td>
                                    <td className="px-2 py-2 text-right tabular-nums">{c.opened}{c.sent > 0 && ` (${Math.round(c.opened / c.sent * 100)}%)`}</td>
                                    <td className="px-2 py-2 text-right tabular-nums">{c.clicked}{c.sent > 0 && ` (${Math.round(c.clicked / c.sent * 100)}%)`}</td>
                                    <td className="px-2 py-2 text-right tabular-nums">{c.unsubscribed}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </Panel>
        </div>
    );
}
